const NAMESPACE_WORKSPACE = 'sankhya.workspace'
const TIPO_HELLO = 'WORKSPACE_HELLO'
const TIPO_NAVEGACAO = 'NAVIGATE'

interface RoteadorWorkspace {
  currentRoute: {
    value: {
      fullPath: string;
    };
  };
  isReady: () => Promise<void>;
  push: (rota: string) => Promise<unknown>;
  resolve: (rota: string) => {
    fullPath: string;
    matched: Array<{ path: string }>;
  };
}

interface OpcoesNavegacaoWorkspace {
  capturarErro?: (erro: Error) => void;
  janela?: Window;
}

interface MensagemWorkspace {
  namespace?: unknown;
  route?: unknown;
  type?: unknown;
}

const janelasInicializadas = new WeakSet<Window>()

const normalizarErro = (erro: unknown): Error => {
  return erro instanceof Error ? erro : new Error(String(erro))
}

const ehRotaInternaSegura = (rota: unknown): rota is string => {
  if (typeof rota !== 'string' || rota !== rota.trim()) {
    return false
  }

  const possuiCaractereDeControle = [...rota].some(caractere => {
    const codigo = caractere.charCodeAt(0)
    return codigo <= 31 || codigo === 127
  })

  return rota.startsWith('/') && !rota.startsWith('//') && !rota.includes('://') && !possuiCaractereDeControle
}

const ehMensagemWorkspace = (mensagem: unknown): mensagem is MensagemWorkspace => {
  return typeof mensagem === 'object' && mensagem !== null && (mensagem as MensagemWorkspace).namespace === NAMESPACE_WORKSPACE
}

export const notificarWorkspacePronto = (janela: Window = window): void => {
  if (janela.parent === janela) {
    return
  }

  janela.parent.postMessage('workspace::ready', '*')
}

export const inicializarNavegacaoWorkspace = (
  roteador: RoteadorWorkspace,
  opcoes: OpcoesNavegacaoWorkspace = {}
): void => {
  const janela = opcoes.janela ?? window

  if (janela.parent === janela || janelasInicializadas.has(janela)) {
    return
  }

  janelasInicializadas.add(janela)

  const capturarErro = opcoes.capturarErro ?? (() => undefined)
  let origemWorkspace: string | null = null
  let roteadorPronto = false
  let versaoHandshake = 0

  const enviarMensagem = (mensagem: object, origem: string): void => {
    janela.parent.postMessage(mensagem, origem)
  }

  const processarHello = async (evento: MessageEvent): Promise<void> => {
    const origemRecebida = evento.origin

    if (
      !origemRecebida ||
      origemRecebida === 'null' ||
      (origemWorkspace !== null && origemRecebida !== origemWorkspace)
    ) {
      return
    }

    origemWorkspace = origemRecebida
    roteadorPronto = false
    const versaoAtual = ++versaoHandshake

    try {
      await roteador.isReady()

      if (versaoAtual !== versaoHandshake || origemWorkspace !== origemRecebida) {
        return
      }

      roteadorPronto = true
      enviarMensagem({ namespace: NAMESPACE_WORKSPACE, type: 'SOLUTION_READY' }, origemRecebida)
    } catch (erro) {
      capturarErro(normalizarErro(erro))
    }
  }

  const processarNavegacao = async (evento: MessageEvent, mensagem: MensagemWorkspace): Promise<void> => {
    if (!roteadorPronto || evento.origin !== origemWorkspace || !ehRotaInternaSegura(mensagem.route)) {
      return
    }

    try {
      const rotaResolvida = roteador.resolve(mensagem.route)

      const rotaEhCatchAll = rotaResolvida.matched.some(registro => registro.path.includes(':pathMatch'))

      if (rotaResolvida.matched.length === 0 || rotaEhCatchAll) {
        return
      }

      await roteador.push(mensagem.route)

      if (roteador.currentRoute.value.fullPath !== rotaResolvida.fullPath) {
        return
      }

      enviarMensagem({
        namespace: NAMESPACE_WORKSPACE,
        type: 'NAVIGATION_SUCCESS',
        route: mensagem.route
      }, origemWorkspace)
    } catch (erro) {
      capturarErro(normalizarErro(erro))
    }
  }

  const processarMensagem = (evento: MessageEvent): void => {
    if (evento.source !== janela.parent || !ehMensagemWorkspace(evento.data)) {
      return
    }

    if (evento.data.type === TIPO_HELLO) {
      void processarHello(evento)
      return
    }

    if (evento.data.type === TIPO_NAVEGACAO) {
      void processarNavegacao(evento, evento.data)
    }
  }

  janela.addEventListener('message', processarMensagem)
}
